import * as monaco from 'monaco-editor/esm/vs/editor/editor.api.js';
// import { Notify } from 'src/notifications';
import {
    Ref,
    // onBeforeUnmount,
    onUnmounted,
    ref,
    shallowRef,
    watch,
} from 'vue';
import { onMounted } from 'vue';
import {
    tsCompilerOptions,
} from '@redviking/argonaut-util/src/argo-lang/parser';

// as far as I can tell, these can only be global.
// parts of this app expect these specific settings.
// if we need to ever change them elsewhere, we'll need to work this into `useMonacoEditor` and `validateScript`.
monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
    noSemanticValidation: false,
    noSyntaxValidation: false,
});
monaco.languages.typescript.javascriptDefaults.setCompilerOptions(tsCompilerOptions as monaco.languages.typescript.CompilerOptions);

export function useMonacoEditor (opts: {
    content: Ref<string>;
    readOnlyRef?: Ref<boolean>;
    language: 'markdown' | 'javascript';
    actions?: monaco.editor.IActionDescriptor[];
    onKeyDown?: (e: monaco.IKeyboardEvent) => void;
    editorOptions?: monaco.editor.IStandaloneEditorConstructionOptions;
}) {
    const editorAnchor = ref<HTMLElement | null>(null);
    const editorRef = shallowRef<monaco.editor.IStandaloneCodeEditor>();
    const model = shallowRef<monaco.editor.ITextModel>();
    onMounted(() => {
        if (!editorAnchor.value) {
            return;
        }

        model.value = monaco.editor.createModel(opts.content.value, opts.language);

        editorRef.value = monaco.editor.create(editorAnchor.value, {
            wordWrap: 'on',
            model: model.value,
            automaticLayout: true,
            showDeprecated: false,
            quickSuggestions: false,
            fixedOverflowWidgets: true,
            readOnly: opts.readOnlyRef?.value,
            minimap: {
                enabled: false,
            },
            suggest: {
                showUnits: false,
                showUsers: false,
                showFields: true,
                showWords: false,
                showFiles: false,
                showEnums: false,
                showColors: false,
                showValues: false,
                showEvents: false,
                showIssues: false,
                showStructs: false,
                showModules: false,
                showClasses: false,
                showFolders: false,
                showKeywords: false,
                showFunctions: true,
                showOperators: false,
                showVariables: false,
                showConstants: false,
                showDeprecated: false,
                showReferences: false,
                showEnumMembers: false,
                showConstructors: false,
                showTypeParameters: false,
            },
            ...(opts.editorOptions || {}),
        });
        for (const action of opts.actions || []) {
            editorRef.value.addAction(action);
        }

        editorRef.value.onDidChangeModelContent(() => {
            opts.content.value = model.value!.getValue();
        });

        editorRef.value.onKeyDown(e => {
            if (opts.onKeyDown) {
                opts.onKeyDown(e);
            }
        });
    });

    if (opts.readOnlyRef) {
        watch(opts.readOnlyRef, readOnly => {
            if (!editorRef.value) {
                return;
            }
            editorRef.value.updateOptions({ readOnly });
        }, { immediate: true });
    }

    watch(opts.content, newContent => {
        if (!model.value) {
            return;
        }

        if (model.value.getValue() !== newContent) {
            model.value.setValue(newContent);
        }
    });

    onUnmounted(() => {
        editorRef.value?.dispose();
        model.value?.dispose();
    });

    return {
        editorRef,
        editorAnchor,
    };
}

export type MonacoDiffEditorOpts = {
    after: Ref<string>;
    before: Ref<string>;
    language: 'markdown' | 'javascript' | 'JSON';
    editorOptions?: monaco.editor.IStandaloneDiffEditorConstructionOptions;
};

export function useMonacoDiffEditor (opts: MonacoDiffEditorOpts) {
    const editorAnchor = ref<HTMLElement | null>(null);
    const editorRef = shallowRef<monaco.editor.IStandaloneDiffEditor>();
    const originalModel = shallowRef<monaco.editor.ITextModel>();
    const modifiedModel = shallowRef<monaco.editor.ITextModel>();
    onMounted(() => {
        if (!editorAnchor.value) {
            return;
        }

        modifiedModel.value = monaco.editor.createModel(opts.after.value, opts.language);
        originalModel.value = monaco.editor.createModel(opts.before.value, opts.language);

        editorRef.value = monaco.editor.createDiffEditor(editorAnchor.value, {
            wordWrap: 'on',
            readOnly: true,
            automaticLayout: true,
            // showDeprecated: false,
            // quickSuggestions: false,
            // fixedOverflowWidgets: true,
            // readOnly: opts.readOnlyRef?.value,
            minimap: {
                enabled: false,
            },
            // suggest: {
            //     showUnits: false,
            //     showUsers: false,
            //     showFields: true,
            //     showWords: false,
            //     showFiles: false,
            //     showEnums: false,
            //     showColors: false,
            //     showValues: false,
            //     showEvents: false,
            //     showIssues: false,
            //     showStructs: false,
            //     showModules: false,
            //     showClasses: false,
            //     showFolders: false,
            //     showKeywords: false,
            //     showFunctions: true,
            //     showOperators: false,
            //     showVariables: false,
            //     showConstants: false,
            //     showDeprecated: false,
            //     showReferences: false,
            //     showEnumMembers: false,
            //     showConstructors: false,
            //     showTypeParameters: false,
            // },
            ...(opts.editorOptions || {}),
        });
        editorRef.value.setModel({
            original: originalModel.value,
            modified: modifiedModel.value,
        });
    });

    onUnmounted(() => {
        editorRef.value?.dispose();
        originalModel.value?.dispose();
        modifiedModel.value?.dispose();
    });

    return {
        editorRef,
        editorAnchor,
    };
}
